import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { axiosInstance } from '../App';
import ImageUpload from './ui/ImageUpload';
import HandymanScheduleCalendar from './HandymanScheduleCalendar';
import {
  Home, Calendar, Clock, Briefcase, MapPin, Navigation,
  Play, CheckCircle, LogOut, User, Phone, Mail, FileText,
  Map as MapIcon, List, Timer, PauseCircle, DollarSign,
  Receipt, Upload, Plus, Eye, Edit, X, Pause, Camera,
  Wrench, AlertCircle
} from 'lucide-react';
import { formatDate, formatDateTime, formatTime } from '../utils/dateUtils';

export default function EnhancedHandymanApp({ user, onLogout }) {
  const [handymanData, setHandymanData] = useState(null);
  const [todaySchedule, setTodaySchedule] = useState([]);
  const [allJobs, setAllJobs] = useState([]);
  const [visits, setVisits] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [jobExpenses, setJobExpenses] = useState({});
  const [activeTimesheet, setActiveTimesheet] = useState(null);
  const [timesheetHistory, setTimesheetHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('home');
  const [scheduleView, setScheduleView] = useState('list');
  
  // Dialog states
  const [selectedJob, setSelectedJob] = useState(null);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showExpenseDialog, setShowExpenseDialog] = useState(false);
  const [showJobExpenseDialog, setShowJobExpenseDialog] = useState(false);
  const [showVisitQuoteDialog, setShowVisitQuoteDialog] = useState(false);
  const [selectedVisit, setSelectedVisit] = useState(null);
  const [showVisitReportDialog, setShowVisitReportDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  
  // Form states
  const [expenseForm, setExpenseForm] = useState({
    category: '',
    description: '',
    amount: '',
    bill_url: ''
  });
  
  const [jobExpenseForm, setJobExpenseForm] = useState({
    expense_details: '',
    amount: '',
    bill_url: ''
  });
  
  const [visitQuoteForm, setVisitQuoteForm] = useState({
    work_description: '',
    includes: '',
    excludes: '',
    estimated_cost: '',
    notes: ''
  });

  // Visit Report form state
  const [visitReportItems, setVisitReportItems] = useState([{ description: '', notes: '', photos: [] }]);
  const [visitReportNotes, setVisitReportNotes] = useState('');
  const [visitReportPhotos, setVisitReportPhotos] = useState([]);
  const [submittingReport, setSubmittingReport] = useState(false);

  const [progressForm, setProgressForm] = useState({
    work_completed: '',
    percentage_complete: 50,
    photos: [],
    notes: ''
  });

  const [completeForm, setCompleteForm] = useState({
    final_photos: [],
    completion_notes: ''
  });

  // Job Scope / Work Details state
  const [jobScopeData, setJobScopeData] = useState({});
  const [loadingJobScope, setLoadingJobScope] = useState(false);
  
  // Job Activity Timeline state
  const [jobTimeline, setJobTimeline] = useState({});
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const handymenRes = await axiosInstance.get('/handymen');
      const handyman = handymenRes.data.find(h => h.email === user.email);
      setHandymanData(handyman);

      if (handyman) {
        const [jobsRes, visitsRes, expensesRes, timesheetRes, timesheetsHistoryRes] = await Promise.all([
          axiosInstance.get(`/jobs?handyman_id=${handyman.id}`),
          axiosInstance.get(`/visit-schedules?handyman_id=${handyman.id}`),
          axiosInstance.get(`/expenses?handyman_id=${handyman.id}`),
          axiosInstance.get(`/timesheets/active/${handyman.id}`),
          axiosInstance.get(`/timesheets?handyman_id=${handyman.id}`)
        ]);

        const jobs = jobsRes.data || [];
        setAllJobs(jobs);
        setVisits(visitsRes.data || []);
        setExpenses(expensesRes.data || []);
        setActiveTimesheet(timesheetRes.data);
        setTimesheetHistory(timesheetsHistoryRes.data || []);

        // Get today's schedule
        const today = new Date().toDateString();
        const todayItems = jobs.filter(j => 
          j.scheduled_date && new Date(j.scheduled_date).toDateString() === today
        );
        setTodaySchedule(todayItems);

        // Fetch job expenses for each job
        const expensesMap = {};
        for (const job of jobs) {
          try {
            const jobExpRes = await axiosInstance.get(`/job-expenses?job_id=${job.id}`);
            expensesMap[job.id] = jobExpRes.data || [];
          } catch (err) {
            expensesMap[job.id] = [];
          }
        }
        setJobExpenses(expensesMap);
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch job scope / work details from linked quote
  const fetchJobScope = async (job) => {
    if (!job || !job.quote_id) {
      setJobScopeData(prev => ({ ...prev, [job?.id]: { items: [], notes: '', hasData: false } }));
      return;
    }
    
    // Return cached data if available
    if (jobScopeData[job.id]?.hasData) return;
    
    setLoadingJobScope(true);
    try {
      const response = await axiosInstance.get(`/quotes/${job.quote_id}`);
      const quote = response.data;
      
      // Extract items without pricing info
      const items = (quote.items || []).map(item => ({
        name: item.name || item.description || 'Service Item',
        quantity: item.quantity || 1,
        unit: item.unit || 'unit',
        description: item.description || item.full_description || '',
        notes: item.notes || '',
        photos: item.photos || []
      }));
      
      setJobScopeData(prev => ({
        ...prev,
        [job.id]: {
          items,
          quoteNotes: quote.internal_notes || quote.notes || '',
          specialInstructions: quote.special_instructions || '',
          hasData: true
        }
      }));
    } catch (error) {
      console.error('Failed to fetch job scope:', error);
      setJobScopeData(prev => ({ ...prev, [job.id]: { items: [], hasData: false } }));
    } finally {
      setLoadingJobScope(false);
    }
  };

  // Fetch job activity timeline
  const fetchJobTimeline = async (jobId) => {
    setLoadingTimeline(true);
    try {
      const [timelineRes, progressRes] = await Promise.all([
        axiosInstance.get(`/job-timeline/${jobId}`),
        axiosInstance.get(`/job-progress/${jobId}`)
      ]);
      
      // Combine timeline events and progress updates
      const allEvents = [
        ...(timelineRes.data || []).map(e => ({...e, source: 'timeline'})),
        ...(progressRes.data || []).map(p => ({
          ...p,
          event_type: 'progress_update',
          source: 'progress'
        }))
      ];
      
      // Sort by timestamp descending (latest first)
      allEvents.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
      
      setJobTimeline(prev => ({ ...prev, [jobId]: allEvents }));
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      setJobTimeline(prev => ({ ...prev, [jobId]: [] }));
    } finally {
      setLoadingTimeline(false);
    }
  };

  // Open job dialog and fetch scope data
  const openJobDetails = (job) => {
    setSelectedJob(job);
    setShowJobDialog(true);
    fetchJobScope(job);
    fetchJobTimeline(job.id);
  };

  const handlePunchIn = async () => {
    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: 'Current Location'
          };

          const response = await axiosInstance.post('/timesheets/punch-in', {
            handyman_id: handymanData.id,
            handyman_name: handymanData.name,
            punch_in_location: location
          });

          setActiveTimesheet({
            id: response.data.timesheet_id,
            punch_in_time: response.data.punch_in_time,
            handyman_id: handymanData.id
          });

          toast.success('Punched in successfully!');
          fetchData();
        }, () => {
          toast.error('Location access denied');
        });
      }
    } catch (error) {
      toast.error('Failed to punch in');
    }
  };

  const handlePunchOut = async () => {
    if (!activeTimesheet) return;

    try {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(async (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };

          const response = await axiosInstance.post(`/timesheets/${activeTimesheet.id}/punch-out`, location);
          toast.success(`Punched out! Total hours: ${response.data.total_hours}`);
          setActiveTimesheet(null);
          fetchData();
        });
      }
    } catch (error) {
      toast.error('Failed to punch out');
    }
  };

  const handleStartTravel = async (job) => {
    try {
      await axiosInstance.post('/job-timeline', null, {
        params: {
          job_id: job.id,
          event_type: 'started_travel',
          handyman_id: handymanData.id
        }
      });

      const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(job.address)}&travelmode=driving`;
      window.open(mapsUrl, '_blank');
      
      toast.success('Started travel! Customer notified.');
      fetchData();
      fetchJobTimeline(job.id);
    } catch (error) {
      toast.error('Failed to start travel');
    }
  };

  const handleStartJob = async (job) => {
    try {
      await axiosInstance.post('/job-timeline', null, {
        params: {
          job_id: job.id,
          event_type: 'started_job',
          handyman_id: handymanData.id
        }
      });

      toast.success('Job started! Customer has been notified.');
      fetchData();
      fetchJobTimeline(job.id);
    } catch (error) {
      toast.error('Failed to start job');
    }
  };

  const handlePauseJob = async (job) => {
    try {
      await axiosInstance.post('/job-timeline', null, {
        params: {
          job_id: job.id,
          event_type: 'paused_job',
          handyman_id: handymanData.id,
          notes: 'Work paused for the day'
        }
      });

      toast.success('Job paused. You can add a daily progress update.');
      setSelectedJob(job);
      setShowProgressDialog(true);
      fetchData();
      fetchJobTimeline(job.id);
    } catch (error) {
      toast.error('Failed to pause job');
    }
  };

  const handleResumeJob = async (job) => {
    try {
      await axiosInstance.post('/job-timeline', null, {
        params: {
          job_id: job.id,
          event_type: 'resumed_job',
          handyman_id: handymanData.id
        }
      });

      toast.success('Job resumed! Customer has been notified.');
      fetchData();
      fetchJobTimeline(job.id);
    } catch (error) {
      toast.error('Failed to resume job');
    }
  };

  const handleSubmitProgress = async () => {
    if (!progressForm.work_completed) {
      toast.error('Please describe the work completed');
      return;
    }

    try {
      await axiosInstance.post('/job-progress', {
        job_id: selectedJob.id,
        work_completed: progressForm.work_completed,
        percentage_complete: progressForm.percentage_complete,
        photos: progressForm.photos,
        notes: progressForm.notes,
        is_daily_update: true
      }, {
        params: {
          handyman_id: handymanData.id,
          handyman_name: handymanData.name
        }
      });

      toast.success('Progress update sent to customer!');
      setShowProgressDialog(false);
      setProgressForm({ work_completed: '', percentage_complete: 50, photos: [], notes: '' });
      fetchData();
      fetchJobTimeline(selectedJob.id);
    } catch (error) {
      toast.error('Failed to submit progress update');
    }
  };

  const handleCompleteJob = async (job) => {
    setSelectedJob(job);
    setShowCompleteDialog(true);
  };

  const handleSubmitCompletion = async () => {
    try {
      await axiosInstance.post('/job-complete', {
        job_id: selectedJob.id,
        final_photos: completeForm.final_photos,
        completion_notes: completeForm.completion_notes
      }, {
        params: {
          handyman_id: handymanData.id
        }
      });

      toast.success('Job completed! Customer has been asked for feedback.');
      setShowCompleteDialog(false);
      setShowJobDialog(false);
      setCompleteForm({ final_photos: [], completion_notes: '' });
      fetchData();
      fetchJobTimeline(selectedJob.id);
    } catch (error) {
      toast.error('Failed to complete job');
    }
  };

  const handleAddJobExpense = async () => {
    try {
      await axiosInstance.post('/job-expenses', {
        job_id: selectedJob.id,
        handyman_id: handymanData.id,
        handyman_name: handymanData.name,
        expense_details: jobExpenseForm.expense_details,
        amount: parseFloat(jobExpenseForm.amount),
        bill_url: jobExpenseForm.bill_url
      });

      // Log expense to timeline
      if (selectedJob) {
        const timelineParams = {
          job_id: selectedJob.id,
          event_type: 'expense_added',
          handyman_id: handymanData.id,
          notes: `Expense: ${jobExpenseForm.expense_details} - $${jobExpenseForm.amount}`
        };
        if (jobExpenseForm.bill_url) {
          timelineParams.photos = jobExpenseForm.bill_url;
        }
        await axiosInstance.post('/job-timeline', null, { params: timelineParams });
        fetchJobTimeline(selectedJob.id);
      }

      toast.success('Job expense added!');
      setShowJobExpenseDialog(false);
      setJobExpenseForm({ expense_details: '', amount: '', bill_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleAddExpense = async () => {
    try {
      await axiosInstance.post('/expenses', {
        handyman_id: handymanData.id,
        handyman_name: handymanData.name,
        category: expenseForm.category,
        description: expenseForm.description,
        amount: parseFloat(expenseForm.amount),
        receipt_url: expenseForm.bill_url,
        submitted_date: new Date().toISOString()
      });

      toast.success('Expense submitted for approval!');
      setShowExpenseDialog(false);
      setExpenseForm({ category: '', description: '', amount: '', bill_url: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to submit expense');
    }
  };

  const handleSaveVisitQuote = async () => {
    // Legacy - kept for compatibility
  };

  const handleStartVisit = async (visit) => {
    try {
      await axiosInstance.post(`/visits/${visit.id}/start`, {});
      toast.success('Visit started');
      fetchData();
    } catch (error) {
      toast.error('Failed to start visit');
    }
  };

  const handleOpenReportDialog = (visit) => {
    setSelectedVisit(visit);
    if (visit.visit_report) {
      setVisitReportItems(visit.visit_report.line_items?.length > 0 
        ? visit.visit_report.line_items 
        : [{ description: '', notes: '', photos: [] }]);
      setVisitReportNotes(visit.visit_report.general_notes || '');
      setVisitReportPhotos(visit.visit_report.general_photos || []);
    } else {
      setVisitReportItems([{ description: '', notes: '', photos: [] }]);
      setVisitReportNotes('');
      setVisitReportPhotos([]);
    }
    setShowVisitReportDialog(true);
  };

  const addReportLineItem = () => {
    setVisitReportItems([...visitReportItems, { description: '', notes: '', photos: [] }]);
  };

  const removeReportLineItem = (index) => {
    if (visitReportItems.length > 1) {
      setVisitReportItems(visitReportItems.filter((_, i) => i !== index));
    }
  };

  const updateReportLineItem = (index, field, value) => {
    const updated = [...visitReportItems];
    updated[index] = { ...updated[index], [field]: value };
    setVisitReportItems(updated);
  };

  const handleSubmitVisitReport = async () => {
    if (!selectedVisit) return;
    const validItems = visitReportItems.filter(item => item.description.trim());
    if (validItems.length === 0) {
      toast.error('Please add at least one work item');
      return;
    }
    setSubmittingReport(true);
    try {
      await axiosInstance.post(`/visits/${selectedVisit.id}/submit-report`, {
        line_items: validItems,
        general_notes: visitReportNotes,
        general_photos: visitReportPhotos
      });
      toast.success('Visit report submitted!');
      setShowVisitReportDialog(false);
      setSelectedVisit(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to submit report');
    } finally {
      setSubmittingReport(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      pending: 'bg-yellow-500',
      assigned: 'bg-purple-500',
      on_the_way: 'bg-indigo-500',
      in_progress: 'bg-orange-500',
      completed: 'bg-green-500',
      cancelled: 'bg-red-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gradient-to-br from-teal-50 to-blue-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-blue-600 text-white p-4 sticky top-0 z-50 shadow-lg">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold">Redefyne</h1>
            <p className="text-sm opacity-90">{handymanData?.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onLogout} className="text-white hover:bg-white/20">
            <LogOut size={18} />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-4 space-y-4">
        {/* HOME TAB */}
        {activeTab === 'home' && (
          <div className="space-y-4">
            {/* Attendance Card */}
            <Card className="shadow-lg border-0">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-bold mb-1">Attendance</h3>
                    {activeTimesheet ? (
                      <p className="text-sm text-gray-600">
                        Punched in at {formatTime(activeTimesheet.punch_in_time)}
                      </p>
                    ) : (
                      <p className="text-sm text-gray-600">Not punched in</p>
                    )}
                  </div>
                  {activeTimesheet ? (
                    <Button onClick={handlePunchOut} className="bg-red-600 hover:bg-red-700">
                      <PauseCircle size={18} className="mr-2" />
                      Punch Out
                    </Button>
                  ) : (
                    <Button onClick={handlePunchIn} className="bg-green-600 hover:bg-green-700">
                      <Timer size={18} className="mr-2" />
                      Punch In
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Today's Schedule */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar size={20} />
                  Today&apos;s Schedule ({todaySchedule.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {todaySchedule.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No jobs scheduled for today</p>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {todaySchedule.map((job) => (
                      <Card key={job.id} className="border-l-4 border-l-teal-500 cursor-pointer hover:shadow-md" onClick={() => openJobDetails(job)}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge className={`${getStatusColor(job.status)} text-white text-xs`}>
                                  {job.status.replace('_', ' ').toUpperCase()}
                                </Badge>
                                <span className="font-medium">{job.service_name}</span>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="flex items-center gap-1">
                                  <User size={14} />
                                  {job.customer_name}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock size={14} />
                                  {formatTime(job.scheduled_date)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4">
              <Card className="shadow-lg border-0">
                <CardContent className="p-4 text-center">
                  <Briefcase size={24} className="mx-auto mb-2 text-teal-600" />
                  <p className="text-2xl font-bold">{allJobs.length}</p>
                  <p className="text-sm text-gray-600">Total Jobs</p>
                </CardContent>
              </Card>
              <Card className="shadow-lg border-0">
                <CardContent className="p-4 text-center">
                  <CheckCircle size={24} className="mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold">
                    {allJobs.filter(j => j.status === 'completed').length}
                  </p>
                  <p className="text-sm text-gray-600">Completed</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* VISITS TAB */}
        {activeTab === 'visits' && (
          <div className="space-y-4" data-testid="handyman-visits-tab">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Scheduled Visits</CardTitle>
                <CardDescription>Visit properties and submit inspection reports</CardDescription>
              </CardHeader>
              <CardContent>
                {visits.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No visits assigned</p>
                ) : (
                  <div className="space-y-3">
                    {visits.sort((a, b) => new Date(b.scheduled_date) - new Date(a.scheduled_date)).map((visit) => (
                      <Card key={visit.id} className="border" data-testid={`visit-card-${visit.id}`}>
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium">{visit.visit_purpose || 'Site Visit'}</h4>
                                <Badge className={
                                  visit.status === 'completed' ? 'bg-green-500 text-white' :
                                  visit.status === 'started' ? 'bg-orange-500 text-white' :
                                  'bg-blue-500 text-white'
                                }>
                                  {visit.status?.toUpperCase()}
                                </Badge>
                              </div>
                              <div className="space-y-1 text-sm">
                                <p className="text-gray-700 flex items-center gap-1">
                                  <User size={14} className="text-gray-400" />
                                  {visit.customer_name}
                                </p>
                                <p className="text-gray-500 flex items-center gap-1">
                                  <MapPin size={14} className="text-gray-400" />
                                  {visit.customer_address}
                                </p>
                                {visit.customer_phone && (
                                  <p className="text-gray-500 flex items-center gap-1">
                                    <Phone size={14} className="text-gray-400" />
                                    {visit.customer_phone}
                                  </p>
                                )}
                                <p className="text-gray-500 flex items-center gap-1">
                                  <Calendar size={14} className="text-gray-400" />
                                  {formatDateTime(visit.scheduled_date)}
                                </p>
                              </div>
                              {visit.notes && (
                                <p className="text-xs text-gray-400 mt-1 italic">Notes: {visit.notes}</p>
                              )}
                              {visit.visit_report && (
                                <div className="mt-2">
                                  <Badge className="bg-green-100 text-green-700">
                                    <CheckCircle size={12} className="mr-1" />
                                    Report Submitted ({visit.visit_report.line_items?.length || 0} items)
                                  </Badge>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            {visit.status === 'scheduled' && (
                              <Button size="sm" onClick={() => handleStartVisit(visit)} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid={`start-visit-${visit.id}`}>
                                <Play size={14} className="mr-1" />
                                Start Visit
                              </Button>
                            )}
                            {(visit.status === 'started' || visit.status === 'scheduled') && !visit.visit_report && (
                              <Button size="sm" onClick={() => handleOpenReportDialog(visit)} className="flex-1" data-testid={`submit-report-${visit.id}`}>
                                <FileText size={14} className="mr-1" />
                                Submit Report
                              </Button>
                            )}
                            {visit.visit_report && (
                              <Button size="sm" variant="outline" onClick={() => handleOpenReportDialog(visit)} className="flex-1" data-testid={`view-report-${visit.id}`}>
                                <Eye size={14} className="mr-1" />
                                View Report
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* SCHEDULE TAB */}
        {activeTab === 'schedule' && (
          <HandymanScheduleCalendar handyman={handymanData} />
        )}

        {/* JOBS TAB - Clickable jobs with full details */}
        {activeTab === 'jobs' && (
          <div className="space-y-4">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>All Jobs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allJobs.map((job) => (
                    <Card key={job.id} className="border cursor-pointer hover:shadow-md" onClick={() => openJobDetails(job)}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <Badge className={`${getStatusColor(job.status)} text-white mb-2`}>
                              {job.status.replace('_', ' ').toUpperCase()}
                            </Badge>
                            <h4 className="font-medium">{job.service_name}</h4>
                            <p className="text-sm text-gray-600">{job.customer_name}</p>
                          </div>
                          <Eye size={18} className="text-gray-400" />
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          <p>📅 {formatDateTime(job.scheduled_date)}</p>
                          <p>📍 {job.address}</p>
                          {job.estimated_hours && <p>⏱️ Est. {job.estimated_hours}h</p>}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* TIMESHEET TAB - Two sections */}
        {activeTab === 'timesheet' && (
          <div className="space-y-4">
            {/* Section 1: Attendance */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Attendance History</CardTitle>
                <CardDescription>Punch in/out records with locations</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {timesheetHistory.slice(0, 10).map((ts) => (
                    <div key={ts.id} className="p-3 border rounded bg-gray-50">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <p className="text-sm font-medium">
                            {formatDate(ts.punch_in_time)}
                          </p>
                          <p className="text-xs text-gray-600">
                            In: {formatTime(ts.punch_in_time)}
                          </p>
                          {ts.punch_out_time && (
                            <p className="text-xs text-gray-600">
                              Out: {formatTime(ts.punch_out_time)}
                            </p>
                          )}
                        </div>
                        {ts.total_hours && (
                          <Badge className="bg-teal-500 text-white">
                            {ts.total_hours}h
                          </Badge>
                        )}
                      </div>
                      {ts.punch_in_location && (
                        <p className="text-xs text-gray-500">
                          📍 {ts.punch_in_location.address || 'Location tracked'}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Job Timeline */}
            <Card className="shadow-lg border-0">
              <CardHeader>
                <CardTitle>Job Timeline</CardTitle>
                <CardDescription>Job start and end times</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {allJobs.filter(j => j.status === 'completed' || j.status === 'in_progress').map((job) => (
                    <div key={job.id} className="p-3 border rounded">
                      <p className="font-medium text-sm">{job.service_name}</p>
                      <p className="text-xs text-gray-600">{job.customer_name}</p>
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        {job.started_at && (
                          <p>🟢 Started: {formatDateTime(job.started_at)}</p>
                        )}
                        {job.completed_at && (
                          <p>✅ Completed: {formatDateTime(job.completed_at)}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* EXPENSES TAB */}
        {activeTab === 'expenses' && (
          <div className="space-y-4">
            <Card className="shadow-lg border-0">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>My Expenses</CardTitle>
                    <CardDescription>Travel, food, accommodation expenses</CardDescription>
                  </div>
                  <Button onClick={() => setShowExpenseDialog(true)}>
                    <Plus size={16} className="mr-2" />
                    Add Expense
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {expenses.map((expense) => (
                    <Card key={expense.id} className="border">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <h4 className="font-medium">{expense.category}</h4>
                            <p className="text-sm text-gray-600">{expense.description}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {formatDate(expense.submitted_date)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold">${expense.amount}</p>
                            <Badge className={
                              expense.status === 'approved' ? 'bg-green-500 text-white' :
                              expense.status === 'rejected' ? 'bg-red-500 text-white' :
                              'bg-yellow-500 text-white'
                            }>
                              {expense.status}
                            </Badge>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {expenses.length === 0 && (
                    <p className="text-gray-500 text-center py-8">No expenses submitted</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Job Details Dialog */}
      <Dialog open={showJobDialog} onOpenChange={setShowJobDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-teal-600" />
              {selectedJob?.service_name}
            </DialogTitle>
            <DialogDescription>Work Order Details</DialogDescription>
          </DialogHeader>
          {selectedJob && (
            <div className="space-y-4">
              {/* Basic Info - NO Amount shown */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500 text-xs">Customer</Label>
                  <p className="font-medium">{selectedJob.customer_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500 text-xs">Status</Label>
                  <Badge className={`${getStatusColor(selectedJob.status)} text-white`}>
                    {selectedJob.status.replace('_', ' ')}
                  </Badge>
                </div>
                <div className="col-span-2">
                  <Label className="text-gray-500 text-xs">Scheduled Date & Time</Label>
                  <p className="font-medium">{formatDateTime(selectedJob.scheduled_date)}</p>
                </div>
              </div>

              <div>
                <Label className="text-gray-500 text-xs">Service Address</Label>
                <p className="font-medium">{selectedJob.address}</p>
              </div>

              {selectedJob.notes && (
                <div>
                  <Label className="text-gray-500 text-xs">Job Notes</Label>
                  <p className="text-sm bg-gray-50 p-2 rounded">{selectedJob.notes}</p>
                </div>
              )}

              {/* Job Scope / Work Details Section */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-800">Job Scope / Work Details</h3>
                </div>
                
                {loadingJobScope ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    <span className="ml-2 text-gray-500">Loading work details...</span>
                  </div>
                ) : jobScopeData[selectedJob.id]?.hasData && jobScopeData[selectedJob.id]?.items?.length > 0 ? (
                  <div className="space-y-4">
                    {/* Items Table */}
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-teal-50">
                          <tr>
                            <th className="text-left p-3 font-medium text-teal-800">Product / Service</th>
                            <th className="text-center p-3 font-medium text-teal-800 w-24">Quantity</th>
                          </tr>
                        </thead>
                        <tbody>
                          {jobScopeData[selectedJob.id].items.map((item, idx) => (
                            <tr key={idx} className="border-t">
                              <td className="p-3" colSpan="2">
                                {/* Service Name & Quantity Row */}
                                <div className="flex justify-between items-start mb-2">
                                  <span className="font-medium text-gray-900">{item.name}</span>
                                  <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium">
                                    {item.quantity} {item.unit}
                                  </span>
                                </div>
                                
                                {/* Full Description */}
                                {item.description && (
                                  <div className="mt-2 text-gray-600 text-sm bg-gray-50 p-2 rounded">
                                    <p className="whitespace-pre-wrap">{item.description}</p>
                                  </div>
                                )}
                                
                                {/* Item Notes */}
                                {item.notes && (
                                  <div className="mt-2 text-orange-700 text-sm bg-orange-50 p-2 rounded flex items-start gap-2">
                                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                                    <span>{item.notes}</span>
                                  </div>
                                )}
                                
                                {/* Item Photos */}
                                {item.photos && item.photos.length > 0 && (
                                  <div className="mt-2">
                                    <p className="text-xs text-gray-500 mb-1 flex items-center gap-1">
                                      <Camera className="w-3 h-3" /> Reference Photos
                                    </p>
                                    <div className="flex gap-2 flex-wrap">
                                      {item.photos.map((photo, photoIdx) => (
                                        <a
                                          key={photoIdx}
                                          href={photo}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="block w-16 h-16 rounded border overflow-hidden hover:opacity-80"
                                        >
                                          <img
                                            src={photo}
                                            alt={`Reference ${photoIdx + 1}`}
                                            className="w-full h-full object-cover"
                                          />
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Special Instructions */}
                    {jobScopeData[selectedJob.id].specialInstructions && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-blue-800 mb-1 flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" /> Special Instructions
                        </p>
                        <p className="text-sm text-blue-700">{jobScopeData[selectedJob.id].specialInstructions}</p>
                      </div>
                    )}
                    
                    {/* Quote Notes */}
                    {jobScopeData[selectedJob.id].quoteNotes && (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs font-medium text-yellow-800 mb-1">Additional Notes</p>
                        <p className="text-sm text-yellow-700">{jobScopeData[selectedJob.id].quoteNotes}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No detailed work scope available</p>
                    <p className="text-gray-400 text-xs mt-1">Work details will appear here when available from the quote</p>
                  </div>
                )}
              </div>

              {/* Job Expenses */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-500 flex items-center gap-1">
                    <DollarSign className="w-4 h-4" /> Job Expenses
                  </Label>
                  <Button size="sm" variant="outline" onClick={() => setShowJobExpenseDialog(true)}>
                    <Plus size={14} className="mr-1" />
                    Add Expense
                  </Button>
                </div>
                <div className="space-y-2">
                  {(jobExpenses[selectedJob.id] || []).length > 0 ? (
                    (jobExpenses[selectedJob.id] || []).map((exp, idx) => {
                      const attachmentUrl = exp.bill_url || exp.receipt_url;
                      const isImage = attachmentUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(attachmentUrl);
                      
                      return (
                      <div key={idx} className="p-3 border rounded text-sm bg-gray-50">
                        <div className="flex justify-between items-start mb-2">
                          <span className="font-medium">{exp.expense_details || exp.item || 'Expense'}</span>
                          <span className="font-bold text-teal-600">${exp.amount}</span>
                        </div>
                        {attachmentUrl && (
                          <div className="mt-2">
                            {isImage ? (
                              <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="block">
                                <img 
                                  src={attachmentUrl} 
                                  alt="Receipt" 
                                  className="max-w-full max-h-32 object-contain rounded border"
                                />
                              </a>
                            ) : (
                              <a href={attachmentUrl} target="_blank" rel="noopener noreferrer" className="text-teal-600 flex items-center gap-1 text-xs hover:underline">
                                <Receipt size={12} /> View Receipt
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                      );
                    })
                  ) : (
                    <p className="text-gray-400 text-sm text-center py-2">No expenses recorded</p>
                  )}
                </div>
              </div>

              {/* Job Activity Timeline */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-teal-600" />
                  <h3 className="font-semibold text-gray-800">Job Activity Timeline</h3>
                  <Badge variant="outline" className="ml-auto">
                    {(jobTimeline[selectedJob.id] || []).length} events
                  </Badge>
                </div>
                
                {loadingTimeline ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-teal-600"></div>
                    <span className="ml-2 text-gray-500">Loading timeline...</span>
                  </div>
                ) : (jobTimeline[selectedJob.id] || []).length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {(jobTimeline[selectedJob.id] || []).map((event, idx) => {
                      // Icon and label based on event type
                      const getEventIcon = (type) => {
                        switch(type) {
                          case 'started_travel': return { icon: Navigation, color: 'text-blue-500', label: 'Started Travel' };
                          case 'started_job': return { icon: Play, color: 'text-green-500', label: 'Job Started' };
                          case 'paused_job': return { icon: Pause, color: 'text-orange-500', label: 'Job Paused' };
                          case 'resumed_job': return { icon: Play, color: 'text-teal-500', label: 'Job Resumed' };
                          case 'completed_job': return { icon: CheckCircle, color: 'text-green-600', label: 'Job Completed' };
                          case 'progress_update': return { icon: FileText, color: 'text-purple-500', label: 'Progress Update' };
                          case 'note_added': return { icon: FileText, color: 'text-gray-500', label: 'Note Added' };
                          case 'photo_uploaded': return { icon: Camera, color: 'text-pink-500', label: 'Photo Uploaded' };
                          case 'expense_added': return { icon: DollarSign, color: 'text-yellow-600', label: 'Expense Added' };
                          default: return { icon: Clock, color: 'text-gray-400', label: type?.replace('_', ' ') || 'Event' };
                        }
                      };
                      
                      const { icon: EventIcon, color, label } = getEventIcon(event.event_type);
                      
                      return (
                        <div key={event.id || idx} className="flex gap-3 p-3 bg-gray-50 rounded-lg">
                          <div className={`w-8 h-8 rounded-full bg-white border-2 flex items-center justify-center flex-shrink-0 ${color}`}>
                            <EventIcon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-medium text-sm text-gray-900">{label}</span>
                              <span className="text-xs text-gray-400">
                                {formatDateTime(event.timestamp)}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              By {event.handyman_name || event.user_name || 'System'}
                            </p>
                            {(event.notes || event.work_completed) && (
                              <p className="text-sm text-gray-700 mt-1 bg-white p-2 rounded border">
                                {event.notes || event.work_completed}
                              </p>
                            )}
                            {event.photos && event.photos.length > 0 && (
                              <div className="flex gap-2 mt-2 flex-wrap">
                                {event.photos.map((photo, pIdx) => (
                                  <a
                                    key={pIdx}
                                    href={photo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block w-12 h-12 rounded border overflow-hidden hover:opacity-80"
                                  >
                                    <img src={photo} alt={`Photo ${pIdx + 1}`} className="w-full h-full object-cover" />
                                  </a>
                                ))}
                              </div>
                            )}
                            {event.percentage_complete !== undefined && (
                              <div className="mt-2">
                                <div className="flex justify-between text-xs mb-1">
                                  <span>Progress</span>
                                  <span>{event.percentage_complete}%</span>
                                </div>
                                <div className="h-1.5 bg-gray-200 rounded-full">
                                  <div 
                                    className="h-full bg-teal-500 rounded-full" 
                                    style={{width: `${event.percentage_complete}%`}}
                                  />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500 text-sm">No activity yet</p>
                    <p className="text-gray-400 text-xs mt-1">Events will appear as you work on this job</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 pt-4 border-t">
                {(selectedJob.status === 'assigned' || selectedJob.status === 'pending') && (
                  <Button onClick={() => handleStartTravel(selectedJob)} className="w-full bg-blue-600 hover:bg-blue-700">
                    <Navigation size={16} className="mr-2" />
                    Start Travel to Location
                  </Button>
                )}

                {selectedJob.status === 'on_the_way' && (
                  <Button onClick={() => handleStartJob(selectedJob)} className="w-full bg-orange-600 hover:bg-orange-700">
                    <Play size={16} className="mr-2" />
                    Start Job
                  </Button>
                )}

                {selectedJob.status === 'in_progress' && (
                  <div className="space-y-2">
                    <Button onClick={() => handlePauseJob(selectedJob)} className="w-full bg-yellow-600 hover:bg-yellow-700">
                      <Pause size={16} className="mr-2" />
                      Pause For The Day
                    </Button>
                    <Button 
                      onClick={() => {
                        setShowProgressDialog(true);
                      }} 
                      variant="outline"
                      className="w-full"
                    >
                      <FileText size={16} className="mr-2" />
                      Add Progress Update
                    </Button>
                    <Button onClick={() => handleCompleteJob(selectedJob)} className="w-full bg-green-600 hover:bg-green-700">
                      <CheckCircle size={16} className="mr-2" />
                      Complete Job
                    </Button>
                  </div>
                )}

                {selectedJob.status === 'paused' && (
                  <div className="space-y-2">
                    <div className="p-3 bg-yellow-50 rounded-lg text-center mb-2">
                      <p className="text-sm text-yellow-800 font-medium">Work is currently paused</p>
                    </div>
                    <Button onClick={() => handleResumeJob(selectedJob)} className="w-full bg-blue-600 hover:bg-blue-700">
                      <Play size={16} className="mr-2" />
                      Resume Work
                    </Button>
                    <Button 
                      onClick={() => setShowProgressDialog(true)} 
                      variant="outline"
                      className="w-full"
                    >
                      <FileText size={16} className="mr-2" />
                      Add Progress Update
                    </Button>
                  </div>
                )}

                <Button variant="outline" onClick={() => setShowJobDialog(false)} className="w-full">
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Job Expense Dialog */}
      <Dialog open={showJobExpenseDialog} onOpenChange={setShowJobExpenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Job Expense</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Expense Details</Label>
              <Input
                value={jobExpenseForm.expense_details}
                onChange={(e) => setJobExpenseForm({...jobExpenseForm, expense_details: e.target.value})}
                placeholder="e.g., Materials purchased"
              />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                value={jobExpenseForm.amount}
                onChange={(e) => setJobExpenseForm({...jobExpenseForm, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <ImageUpload
                label="Bill/Receipt Photo"
                multiple={false}
                folder="receipts"
                existingImages={jobExpenseForm.bill_url ? [jobExpenseForm.bill_url] : []}
                onUpload={(url) => setJobExpenseForm({...jobExpenseForm, bill_url: url || ''})}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowJobExpenseDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleAddJobExpense} className="flex-1">
                Add Expense
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Regular Expense Dialog */}
      <Dialog open={showExpenseDialog} onOpenChange={setShowExpenseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Expense</DialogTitle>
            <DialogDescription>Submit expense for admin approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <select
                value={expenseForm.category}
                onChange={(e) => setExpenseForm({...expenseForm, category: e.target.value})}
                className="w-full p-2 border rounded"
              >
                <option value="">Select category</option>
                <option value="Travel">Travel</option>
                <option value="Food">Food</option>
                <option value="Accommodation">Accommodation</option>
                <option value="Tools">Tools</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                placeholder="Expense details..."
              />
            </div>
            <div>
              <Label>Amount ($)</Label>
              <Input
                type="number"
                value={expenseForm.amount}
                onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                placeholder="0.00"
              />
            </div>
            <div>
              <ImageUpload
                label="Bill/Receipt Photo"
                multiple={false}
                folder="receipts"
                existingImages={expenseForm.bill_url ? [expenseForm.bill_url] : []}
                onUpload={(url) => setExpenseForm({...expenseForm, bill_url: url || ''})}
              />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowExpenseDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleAddExpense} className="flex-1">
                Submit for Approval
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Visit Quote Details Dialog */}
      {/* Visit Report Dialog */}
      <Dialog open={showVisitReportDialog} onOpenChange={setShowVisitReportDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedVisit?.visit_report ? 'Visit Report' : 'Submit Visit Report'}</DialogTitle>
            <DialogDescription>
              {selectedVisit?.customer_name} - {selectedVisit?.customer_address}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Work Items</Label>
                {!selectedVisit?.visit_report && (
                  <Button size="sm" variant="outline" onClick={addReportLineItem} data-testid="add-line-item-btn">
                    <Plus size={14} className="mr-1" /> Add Item
                  </Button>
                )}
              </div>
              <div className="space-y-3">
                {visitReportItems.map((item, index) => (
                  <Card key={index} className="border p-3">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Item {index + 1}</Label>
                        {!selectedVisit?.visit_report && visitReportItems.length > 1 && (
                          <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => removeReportLineItem(index)}>
                            <X size={14} className="text-red-500" />
                          </Button>
                        )}
                      </div>
                      <Input
                        placeholder="Work description (e.g., Replace kitchen faucet)"
                        value={item.description}
                        onChange={(e) => updateReportLineItem(index, 'description', e.target.value)}
                        readOnly={!!selectedVisit?.visit_report}
                        data-testid={`line-item-desc-${index}`}
                      />
                      <Textarea
                        placeholder="Additional notes for this item..."
                        value={item.notes || ''}
                        onChange={(e) => updateReportLineItem(index, 'notes', e.target.value)}
                        readOnly={!!selectedVisit?.visit_report}
                        rows={2}
                        className="text-sm"
                      />
                      {/* Photos for this line item */}
                      <div>
                        <Label className="text-xs text-gray-500 mb-1 block">Photos</Label>
                        {!selectedVisit?.visit_report && (
                          <ImageUpload
                            onUpload={(url) => {
                              const updated = [...visitReportItems];
                              updated[index] = { ...updated[index], photos: [...(updated[index].photos || []), url] };
                              setVisitReportItems(updated);
                            }}
                            folder="visit-reports"
                          />
                        )}
                        {item.photos && item.photos.length > 0 && (
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {item.photos.map((photo, pi) => (
                              <div key={pi} className="relative">
                                <img src={photo} alt={`Item ${index + 1} photo ${pi + 1}`} className="w-16 h-16 object-cover rounded border" />
                                {!selectedVisit?.visit_report && (
                                  <button
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                                    onClick={() => {
                                      const updated = [...visitReportItems];
                                      updated[index] = { ...updated[index], photos: updated[index].photos.filter((_, i) => i !== pi) };
                                      setVisitReportItems(updated);
                                    }}
                                  >x</button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>

            {/* General Notes */}
            <div>
              <Label>General Notes</Label>
              <Textarea
                value={visitReportNotes}
                onChange={(e) => setVisitReportNotes(e.target.value)}
                placeholder="Overall observations, recommendations..."
                rows={3}
                readOnly={!!selectedVisit?.visit_report}
                data-testid="report-general-notes"
              />
            </div>

            {/* General Photos */}
            <div>
              <Label>General Photos</Label>
              {!selectedVisit?.visit_report && (
                <ImageUpload
                  onUpload={(url) => setVisitReportPhotos([...visitReportPhotos, url])}
                  folder="visit-reports"
                />
              )}
              {visitReportPhotos.length > 0 && (
                <div className="flex gap-2 mt-2 flex-wrap">
                  {visitReportPhotos.map((photo, i) => (
                    <div key={i} className="relative">
                      <img src={photo} alt={`General photo ${i + 1}`} className="w-20 h-20 object-cover rounded border" />
                      {!selectedVisit?.visit_report && (
                        <button
                          className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs"
                          onClick={() => setVisitReportPhotos(visitReportPhotos.filter((_, idx) => idx !== i))}
                        >x</button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowVisitReportDialog(false)} className="flex-1">
                {selectedVisit?.visit_report ? 'Close' : 'Cancel'}
              </Button>
              {!selectedVisit?.visit_report && (
                <Button onClick={handleSubmitVisitReport} disabled={submittingReport} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-visit-report-btn">
                  {submittingReport ? 'Submitting...' : 'Submit Report'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Progress Update Dialog */}
      <Dialog open={showProgressDialog} onOpenChange={setShowProgressDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} className="text-teal-600" />
              Daily Progress Update
            </DialogTitle>
            <DialogDescription>
              Update the customer and admin on today&apos;s work progress
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Work Completed Today *</Label>
              <Textarea
                value={progressForm.work_completed}
                onChange={(e) => setProgressForm({...progressForm, work_completed: e.target.value})}
                placeholder="Describe what work was completed today..."
                rows={3}
              />
            </div>

            <div>
              <Label>Overall Progress: {progressForm.percentage_complete}%</Label>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={progressForm.percentage_complete}
                onChange={(e) => setProgressForm({...progressForm, percentage_complete: parseInt(e.target.value)})}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>0%</span>
                <span>25%</span>
                <span>50%</span>
                <span>75%</span>
                <span>100%</span>
              </div>
            </div>

            <div>
              <ImageUpload
                label="Work Progress Photos"
                multiple={true}
                maxFiles={5}
                folder="progress"
                existingImages={progressForm.photos}
                onUpload={(urls) => setProgressForm({...progressForm, photos: urls})}
              />
            </div>

            <div>
              <Label>Additional Notes</Label>
              <Input
                value={progressForm.notes}
                onChange={(e) => setProgressForm({...progressForm, notes: e.target.value})}
                placeholder="Any additional notes..."
              />
            </div>

            <div className="p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-900">
                <strong>This update will be sent to:</strong>
                <br />• Customer (job owner)
                <br />• Admin team
                <br />• Recorded in timesheet reports
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowProgressDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmitProgress} className="flex-1 bg-teal-600 hover:bg-teal-700">
                <CheckCircle size={16} className="mr-2" />
                Submit Update
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Job Completion Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle size={20} className="text-green-600" />
              Complete Job
            </DialogTitle>
            <DialogDescription>
              Upload final photos and submit for customer feedback
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-800 mb-2">Job: {selectedJob?.service_name}</h4>
              <p className="text-sm text-green-700">Customer: {selectedJob?.customer_name}</p>
            </div>

            <div>
              <ImageUpload
                label="Final Completed Work Photos *"
                multiple={true}
                maxFiles={10}
                folder="completed"
                existingImages={completeForm.final_photos}
                onUpload={(urls) => setCompleteForm({...completeForm, final_photos: urls})}
              />
            </div>

            <div>
              <Label>Completion Notes</Label>
              <Textarea
                value={completeForm.completion_notes}
                onChange={(e) => setCompleteForm({...completeForm, completion_notes: e.target.value})}
                placeholder="Summary of work completed, any recommendations for the customer..."
                rows={3}
              />
            </div>

            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <p className="text-xs text-yellow-900">
                <strong>⚠️ After completion:</strong>
                <br />• Customer will receive notification to rate the service
                <br />• Final photos will be shared with customer
                <br />• Job will be marked as completed
                <br />• Invoice will be generated (if applicable)
              </p>
            </div>

            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowCompleteDialog(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmitCompletion} className="flex-1 bg-green-600 hover:bg-green-700">
                <CheckCircle size={16} className="mr-2" />
                Complete & Submit for Feedback
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation - 6 tabs */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg">
        <div className="grid grid-cols-6 gap-1 p-2">
          <Button
            variant={activeTab === 'home' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('home')}
            className="flex-col h-auto py-2"
          >
            <Home size={18} />
            <span className="text-xs mt-1">Home</span>
          </Button>
          <Button
            variant={activeTab === 'visits' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('visits')}
            className="flex-col h-auto py-2"
          >
            <FileText size={18} />
            <span className="text-xs mt-1">Visits</span>
          </Button>
          <Button
            variant={activeTab === 'schedule' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('schedule')}
            className="flex-col h-auto py-2"
          >
            <Calendar size={18} />
            <span className="text-xs mt-1">Schedule</span>
          </Button>
          <Button
            variant={activeTab === 'jobs' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('jobs')}
            className="flex-col h-auto py-2"
          >
            <Briefcase size={18} />
            <span className="text-xs mt-1">Jobs</span>
          </Button>
          <Button
            variant={activeTab === 'timesheet' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('timesheet')}
            className="flex-col h-auto py-2"
          >
            <Clock size={18} />
            <span className="text-xs mt-1">Time</span>
          </Button>
          <Button
            variant={activeTab === 'expenses' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('expenses')}
            className="flex-col h-auto py-2"
          >
            <Receipt size={18} />
            <span className="text-xs mt-1">Expenses</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
